import { useLayoutEffect, useState } from 'react';
import { Link } from 'react-router';

import { useMutation } from '@tanstack/react-query';

import { ApiError } from '@mds/shared/lib/api';
import { cn } from '@mds/shared/lib/utils';
import { WORK_FIELDS } from '@mds/shared/types/job-application';
import { Button } from '@mds/shared/ui/shadcn/button';
import { Input } from '@mds/shared/ui/shadcn/input';
import Select from '@mds/shared/ui/select';
import { Field } from '@mds/shared/ui/form/field';
import { Form, applyServerErrors, useZodForm } from '@mds/shared/ui/form/form';
import { PhoneInput } from '@mds/shared/ui/form/phone-input';

import CvDrop from '@/components/form/cv-drop';
import RotatingWord from '@/components/heading/rotating-word';
import CrewBadge, { type BadgeRow, type BadgeStatus } from '@/components/illustration/crew-badge';
import { ErrorBlock } from '@/components/states';
import { sendApplication } from '@/features/careers/careers.api';
import {
  CV_EXTENSIONS,
  CV_MAX_BYTES,
  careersSchema,
  type CareersForm,
} from '@/features/careers/careers.schema';

import { CAREERS } from '@/data';

/** The form's fields, each counting towards the badge's "n of 5 complete". */
const FIELDS = ['fullName', 'email', 'phone', 'field', 'cv'] as const;

/** The badge's checklist: every field but the name, which is the badge's title. */
const BADGE_ROWS: { label: string; key: (typeof FIELDS)[number] }[] = [
  { label: 'Email', key: 'email' },
  { label: 'Phone', key: 'phone' },
  { label: 'Role', key: 'field' },
  { label: 'CV', key: 'cv' },
];

const FIELD_OPTIONS = WORK_FIELDS.map((field) => ({ value: field, label: field }));
const EMPTY: CareersForm = { fullName: '', email: '', phone: '', field: '', cv: null };

/** Whether a value has been started on: typed into, picked, attached. */
const isStarted = (value: unknown) =>
  value instanceof File || (typeof value === 'string' && value.trim() !== '');

export default function CareersPage() {
  // Errors wait for the first send, then follow the typing as it fixes them.
  const form = useZodForm(careersSchema, {
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    defaultValues: EMPTY,
  });
  const values = form.watch();
  const tried = form.formState.isSubmitted;

  /**
   * A CV refused on pick, with the file that was attached at the time. Shown
   * in place of the schema's message only while that is still the file, so
   * attaching or removing one clears it without an effect.
   */
  const [cvRejected, setCvRejected] = useState<{ message: string; over: File | null } | null>(
    null,
  );
  const cvError = cvRejected && cvRejected.over === values.cv ? cvRejected.message : undefined;

  /** A failure no field can show, under the button. */
  const [formError, setFormError] = useState<unknown>(null);

  const sending = useMutation({
    mutationFn: sendApplication,
    onMutate: () => setFormError(null),
    onError: (error) => {
      // Field mistakes the form's own checks did not catch land under the field.
      if (applyServerErrors(error, form.setError)) return;

      const code = error instanceof ApiError ? error.code : undefined;
      /** Said under the drop zone, rather than in the alert under the button. */
      const cvRefused = (message: string) =>
        form.setError('cv', { type: 'server', message }, { shouldFocus: true });

      if (code === 'CV_REQUIRED') return cvRefused('Attach your CV.');
      if (code === 'CV_TOO_LARGE') return cvRefused('Your CV is over 10 MB. Attach a smaller file.');
      if (code === 'INVALID_CV' || code === 'INVALID_CV_TYPE') {
        return cvRefused("We couldn't read that file. Attach a PDF or Word document.");
      }
      // The monthly limit (worded below), the rate limit, an outage: under the
      // button.
      setFormError(error);
    },
  });

  // Each field checked against its own rule, so the badge ticks a row the
  // moment it is right rather than when the whole form is sent.
  const status = Object.fromEntries(
    FIELDS.map((key): [string, BadgeStatus] => {
      const valid = careersSchema.shape[key].safeParse(values[key]).success;
      if (valid) return [key, 'done'];
      return [key, tried || isStarted(values[key]) ? 'missing' : 'idle'];
    }),
  ) as Record<(typeof FIELDS)[number], BadgeStatus>;

  const complete = FIELDS.filter((key) => status[key] === 'done').length;
  const progressLabel = `${complete} of ${FIELDS.length} complete`;
  const rows: BadgeRow[] = BADGE_ROWS.map(({ label, key }) => ({ label, status: status[key] }));

  const sent = sending.isSuccess ? sending.variables : null;
  const firstName = sent?.fullName.split(/\s+/)[0] || 'friend';

  // The confirmation is shorter than the form it replaces, and the visitor
  // was down at the Send button: start them at the top, before the paint.
  useLayoutEffect(() => {
    if (sent) window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
  }, [sent]);

  return (
    // The badge's column pulls up by the section's top padding from `lg`, so
    // the strap starts at the header's bottom edge and the badge hangs from it.
    <section className="mx-auto grid max-w-page items-start gap-block px-gutter pt-block lg:grid-cols-2">
      <div className="flex flex-col gap-8">
        <header className="flex flex-col gap-4">
          <h1 className="text-heading-xl text-balance">{CAREERS.title}</h1>
          <RotatingWord lead={CAREERS.lookingFor} words={CAREERS.words} />
        </header>

        {sent ? (
          // In place of the form: a form still sitting there invites sending twice.
          <div
            role="status"
            className="flex flex-col items-start gap-4 rounded-card border border-hairline bg-surface p-card"
          >
            <span className="font-mono text-2xs text-brand-text uppercase">
              Application received
            </span>
            <h2 className="text-heading-lg">Thanks, {firstName}. Your badge is on our desk.</h2>
            <p className="max-w-prose text-base text-ink-secondary">
              Our doors are always open to great people. When the right role opens, you&apos;ll
              hear from us.
            </p>
            <div className="flex flex-wrap gap-3 pt-2">
              <Button asChild>
                <Link to="/">Back to MDS</Link>
              </Button>
            </div>
          </div>
        ) : (
          <Form
            form={form}
            onSubmit={(application) => sending.mutate(application)}
            className="flex flex-col gap-5"
          >
            <Field name="fullName" label="Full name" required>
              <Input autoComplete="name" />
            </Field>

            <div className="grid gap-5 sm:grid-cols-2">
              <Field name="email" label="Email" required>
                <Input type="email" autoComplete="email" />
              </Field>
              <Field name="phone" label="Phone number" required>
                {/* Over the input's own "70 123 456" sample. */}
                <PhoneInput placeholder="" />
              </Field>
            </div>

            <Field name="field" label="Role" required>
              <Select variant="field" options={FIELD_OPTIONS} placeholder="Choose a role" />
            </Field>

            <Field name="cv" label="Your CV" required error={cvError}>
              <CvDrop
                extensions={CV_EXTENSIONS}
                maxBytes={CV_MAX_BYTES}
                hint="PDF or Word, up to 10 MB"
                onReject={(message) => setCvRejected({ message, over: values.cv })}
              />
            </Field>

            {formError !== null && (
              <ErrorBlock
                title="We couldn't send your application"
                error={formError}
                message={
                  formError instanceof ApiError && formError.code === 'JOB_APPLICATION_MONTHLY_LIMIT'
                    ? 'You can submit one application per month.'
                    : undefined
                }
                onDismiss={() => setFormError(null)}
              />
            )}

            {/* Full width on a phone, where the badge's strap hangs from it
                (and passes behind it, hence the lift); its own width beside
                the badge from `lg`. */}
            <Button
              type="submit"
              disabled={sending.isPending}
              className="relative z-1 w-full lg:w-auto lg:self-start"
            >
              {sending.isPending ? 'Sending…' : 'Send application'}
            </Button>
          </Form>
        )}
      </div>

      {/* Pulled up by the gap it would otherwise have above it. Beside the
          form, the strap comes out of the bottom of the header. Stacked, it is
          pulled up half the Send button's height further and passes behind
          it, so it reads as coming out of the button, the pivot it swings on
          hidden inside. Once sent there is no Send button, and the same pull
          would push the strap up through the confirmation's buttons: it hangs
          from the confirmation card's bottom edge instead. */}
      <div
        className={cn(
          'flex justify-center lg:sticky lg:top-header lg:-mt-block',
          sent ? 'max-lg:-mt-block' : 'max-lg:-mt-[calc(var(--spacing-block)+(--spacing(6)))]',
        )}
      >
        <CrewBadge
          name={values.fullName}
          rows={rows}
          progress={complete / FIELDS.length}
          progressLabel={progressLabel}
          received={Boolean(sent)}
        />
      </div>
    </section>
  );
}
